import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import SectionHeader from "../../components/views/SectionHeader";
import SectionFooter from "../../components/views/SectionFooter";
import InputField from "../../components/inputs/inputField";
import FormBuilderModal from "../event/forms/modals/formBuilder";
import PagePreviewFrame from "../../views/pages/components/pagePreviewFrame";
import ToggleField from "../../components/inputs/toggleField";
import SelectField from "../../components/inputs/selectField";
import RichTextEditor from "../../views/pages/components/richTextEditor";
import PageEditor from "../../components/modals/pageEditor";
import TabsContainer from "../../components/views/TabsContainer";
import API from "../../utils/API";

const durationOptions = [
	{ value: "minutes", label: "Minutes" },
	{ value: "hours", label: "Hours" },
];

const ENV =
	process.env.NODE_ENV === "production"
		? window.location.href.includes("planner.simple.events")
			? `https://admin-api.simple.events/`
			: `https://beta-api.simple.events/`
		: `http://${window.location.hostname}:5015/`;

const tabs = [
	{ title: "Details", id: "details" },
	{ title: "Setup", id: "setup" },
	{ title: "Preview", id: "preview" },
];

// Booking-specific base field list with calendar selection
const bookingBaseFieldList = [
	{
		_id: "customer",
		title: "Customer Information",
		fields: [
			{
				_id: "_firstName",
				id: "firstName",
				fieldName: "First Name",
				fieldType: "text",
				propertyType: "base",
				required: true,
				unique: true,
			},
			{
				_id: "_lastName",
				id: "lastName",
				fieldName: "Last Name",
				fieldType: "text",
				propertyType: "base",
				required: false,
			},
			{
				_id: "_email",
				id: "email",
				fieldName: "Email",
				fieldType: "email",
				propertyType: "base",
				required: true,
				unique: true,
			},
			{
				_id: "_phoneNumber",
				id: "phoneNumber",
				fieldName: "Phone Number",
				fieldType: "tel",
				propertyType: "base",
				required: false,
			},
			{
				_id: "_company",
				id: "company",
				fieldName: "Company",
				fieldType: "text",
				propertyType: "base",
				required: false,
			},
		],
	},
	{
		_id: "booking",
		title: "Booking Details",
		fields: [
			{
				_id: "_bookingCalendar",
				fieldName: "Booking Calendar",
				fieldType: "bookingCalendar",
				propertyType: "base",
				moduleConfiguration: {
					allowSelection: true,
					defaultCalendar: null,
				},
			},
			{
				_id: "_meetingPurpose",
				fieldName: "Meeting Purpose",
				fieldType: "textarea",
				propertyType: "base",
				required: false,
			},
			{
				_id: "_specialRequests",
				fieldName: "Special Requests",
				fieldType: "textarea",
				propertyType: "base",
				required: false,
			},
			{
				_id: "_attendeeCount",
				fieldName: "Number of Attendees",
				fieldType: "number",
				propertyType: "base",
				required: false,
			},
			{
				_id: "_reservedDateTimes",
				fieldName: "Reserved Date Times",
				fieldType: "reservedDateTimes",
				propertyType: "base",
				required: false,
			},
		],
	},
];

class BookingFormBuilder extends Component {
	constructor(props) {
		super(props);

		const eventOptions = [];
		props.eventMap &&
			Object.keys(props.eventMap).forEach((eventId) => {
				const event = props.eventMap[eventId];
				eventOptions.push({ value: eventId, label: event.name });
			});

		this.state = {
			editedBookingPage: { ...props.bookingPage },
			form: props.bookingPage.form,
			editedForm: JSON.parse(JSON.stringify(props.bookingPage.form)),
			page: props.bookingPage.page,
			editedPage: JSON.parse(JSON.stringify(props.bookingPage.page)),
			editing: false,
			formBuilderModalOpen: false,
			pageBuilderModalOpen: false,
			editCount: 0,
			eventOptions: eventOptions,
			selectedSection: "details",
			requiredFields: {
				name: false,
				meetingDuration: false,
				pageSlug: false,
			},
			formIsValid: true,
		};
	}

	componentDidUpdate(prevProps) {
		if (prevProps.calendars !== this.props.calendars) {
			// Update calendar options in form when calendars change
			this.updateCalendarFieldOptions();
		}
	}

	updateCalendarFieldOptions = () => {
		const { editedForm } = this.state;
		const { calendars } = this.props;

		// Find and update booking calendar fields
		const updatedForm = { ...editedForm };
		updatedForm.pages.forEach((page) => {
			page.sections.forEach((section) => {
				section.fields.forEach((field) => {
					if (field.type === "bookingCalendar") {
						field.moduleConfiguration = {
							...field.moduleConfiguration,
							availableCalendars: calendars?.map((cal) => cal._id) || [],
							allowSelection: calendars?.length > 1,
						};
					}
				});
			});
		});

		this.setState({ editedForm: updatedForm, editing: true });
	};
	validateForm = () => {
		const { editedBookingPage } = this.state;
		let formIsValid = true;

		if (!editedBookingPage.name || editedBookingPage.name.trim() === '') {
			this.setState({ 
				requiredFields: { ...this.state.requiredFields, name: true },
				formIsValid: false 
			});
			formIsValid = false;
		} else {
			this.setState({ 
				requiredFields: { ...this.state.requiredFields, name: false },
				formIsValid: true 
			});
		}

		return formIsValid;
	};

	handleInputChange = (field, value) => {
		this.setState({
			editedBookingPage: {
				...this.state.editedBookingPage,
				[field]: value,
			},
			editing: true,
		}, () => {
			if (field === 'name') {
				this.validateForm();
			}
		});
	};

	saveBookingPage = () => {
		if (!this.validateForm()) {
			return;
		}

		const { editedBookingPage } = this.state;
		this.props.saveBookingPageDetails(editedBookingPage, () => {
			this.setState({ 
				editing: false, 
				editCount: this.state.editCount + 1,
				requiredFields: {
					name: false,
					meetingDuration: false,
					pageSlug: false,
				}
			});
		});
	};

	cancelEdit = () => {
		this.setState({
			editedBookingPage: { ...this.props.bookingPage },
			editing: false,
		});
	};

	handleChange = (field, value) => {
		this.setState({
			editedBookingPage: {
				...this.state.editedBookingPage,
				[field]: value,
			},
			editing: true,
		});
	};
	saveForm = async () => {
		const { orgId } = this.props;
		const { editedForm } = this.state;
		let editedFormTemp = JSON.parse(JSON.stringify(editedForm));
		if (editedForm.confirmationEmail == 'N/A' || editedForm.confirmationEmail == '') {
			delete editedFormTemp.confirmationEmail;
		}
		await API()
			.patch(`Organizations/${orgId}/accountForms/${editedForm._id}`, editedFormTemp)
			.then((res) => {
				if (res.data) {
					this.setState({
						form: { ...editedForm, ...res.data },
						editing: false
					});
				}
			})
			.catch((e) => {
				this.setState({
					invalid: true,
					submitting: false
				});
			});
	};


	updateForm = (updatedForm) => {
		this.setState({
			editedForm: updatedForm,
			editing: true,
		});
	};


	updatePage = (updatedPage) => {
		this.setState({
			editedPage: updatedPage,
			editing: true,
		});
	};

	toggleFormBuilderModal = () => {
		this.setState({ formBuilderModalOpen: !this.state.formBuilderModalOpen });
	};

	togglePageBuilderModal = () => {
		this.setState({ pageBuilderModalOpen: !this.state.pageBuilderModalOpen });
	};

	handlePageSlugChange = (e) => {
		const value = e.target.value
			.trim()
			.toLowerCase()
			.replace(/[^a-z0-9-]/g, "-");
		this.setState({
			editedBookingPage: {
				...this.state.editedBookingPage,
				page: {
					...this.state.editedBookingPage.page,
					pathSlug: value,
				},
			},
			editing: true,
		});
	};

	updateSelected = (index) => {
		this.setState({ selectedSection: tabs[index].id });
	};

	render() {
		const {
			form,
			editedForm,
			editedPage,
			editing,
			formBuilderModalOpen,
			pageBuilderModalOpen,
			editedBookingPage,
			selectedSection,
			requiredFields,
		} = this.state;
		const { bookingPage, sectionName, orgId, calendars } = this.props;
		return (
			<div className="sectionContainer">
				<SectionHeader
					title={sectionName}
					description="Design the booking form fields and page layout that customers will use"
					breadcrumbs={[
						{
							name: bookingPage.name || "Booking Page",
						},
					]}
				>
					<TabsContainer
						tabs={tabs}
						updateSelected={this.updateSelected}
						selectedIndex={tabs.findIndex((tab) => tab.id === selectedSection)}
					/>
				</SectionHeader>
				{selectedSection === "details" && (
					<div className="sectionBody">
						<div className="blocksContainer">
							<div className="flex w-100 gap20">
								<div className="sectionBlock contained-h">
									<h2 className="blockHeader mt-10">Basic Information</h2>

									<InputField
										label="Booking Name"
										placeholder="e.g., Consultation Booking, Team Meeting"
										value={editedBookingPage.name}
										required={true}
										errorTrigger={requiredFields.name}
										errorMessage="Booking page name is required"
										onChange={(e) =>
											this.handleInputChange("name", e.target.value)
										}
									/>

									<RichTextEditor
										id="bookingPageSubtitle"
										label="Booking Message"
										className="formFieldContainer posRel"
										placeholder="Optional message for the booking page"
										information="This message will appear below the booking name on the booking form."
										value={editedBookingPage.subtitle}
										defaultHeight={300}
										onChange={(value) =>
											this.handleInputChange("subtitle", value)
										}
									/>

									<InputField
										label="Description"
										placeholder="Brief description of what this booking page is for..."
										value={editedBookingPage.description}
										onChange={(e) =>
											this.handleInputChange("description", e.target.value)
										}
									/>

									<h2 className="blockHeader mt-10">Booking Details</h2>
									<div className="flex w-100 gap20">
										<InputField
											label="Meeting Duration"
											value={editedBookingPage.meetingDuration}
											errorTrigger={requiredFields.meetingDuration}
											errorMessage="Meeting duration is required"
											type="number"
											max={
												editedBookingPage.durationUnit === "minutes" ? 1440 : 24
											}
											onChange={(e) => {
												const value = e.target.value;
												if (
													!(
														value < 1 ||
														(editedBookingPage.durationUnit === "minutes" &&
															value > 1440) ||
														(editedBookingPage.durationUnit === "hours" &&
															value > 24)
													)
												) {
													this.handleInputChange(
														"meetingDuration",
														e.target.value
													);
												}
											}}
										/>
										<SelectField
											label="Duration Unit"
											value={durationOptions.find(
												(opt) => opt.value === editedBookingPage.durationUnit
											)}
											options={durationOptions}
											required={true}
											errorTrigger={requiredFields.durationUnit}
											onChange={(option) =>
												this.handleInputChange("durationUnit", option.value)
											}
										/>
									</div>

										<ToggleField
											label="Limit reservations per single booking request"
											information="If enabled, users can only make limited reservations at a time otherwise all slots are available."
											checked={editedBookingPage.maxReservationsEnabled}
											onChange={(e) =>
												this.handleInputChange(
													"maxReservationsEnabled",
													e.target.checked
												)
											}
											icons={false}
										/>
									<div className="flex aic gap20">
										<InputField
											label="Max Reservations"
											placeholder="e.g., 1, 5, 10"
											type={
												editedBookingPage.maxReservationsEnabled ? "number" : "text"
											}
											min={1}
											disabled={!editedBookingPage.maxReservationsEnabled}
											value={
												editedBookingPage.maxReservationsEnabled
													? editedBookingPage.maxReservations || 1
													: "All Available"
											}
											onChange={(e) =>
												this.handleInputChange(
													"maxReservations",
													e.target.value
												)
											}
											inputIcon={
												!editedBookingPage.limitReservations && (
													<i className="las la-lock" />
												)
											}
										/>
									</div>

									{editedBookingPage.isPartofEvent && editedBookingPage.eventId?._id && (
										<>
											<h2 className="blockHeader mt-10">Integration</h2>
											<ToggleField
												label="Connect to Event"
												icons={false}
												checked={editedBookingPage.isPartofEvent}
												onChange={(e) =>
													this.handleInputChange("isPartofEvent", e.target.checked)
												}
											/>
											{/* {editedBookingPage.isPartofEvent && (
												<SelectField
													label="Select Event"
													value={this.state.eventOptions.find(
														(option) =>
															option.value === editedBookingPage?.eventId?._id
													)}
													options={this.state.eventOptions}
													onChange={(value) =>
														this.handleInputChange("eventId._id", value)
													}
													disabled={true}
													inputIcon={<i className="las la-lock" />}
												/>
											)} */}
											<InputField
												label="Connected Event"
												value={editedBookingPage.eventId?.name || 'Event Not Found'}
												disabled={true}
												inputIcon={<i className="las la-lock" />}
											/>
										</>

									)}
								</div>

								<div className="emailDesignPreview w-100 mt-10">
									<div
										style={{
											maxWidth: "calc(100% - 15px)",
											minWidth: "calc(100% - 700px)",
										}}
										className="cardDetailsContainerPages containerTitle mb-20"
									>
										<div className="w-100">
											<h2 className="blockHeader m-0">Booking Page Preview</h2>
											<p>Booking page as attendees will see</p>
										</div>
										<div className="flex aic gap10">
											<button
												className={`neu ${editing ? " disabled" : ""}`}
												disabled={editing}
												onClick={() => {
													this.setState({ pageBuilderModalOpen: true });
												}}
											>
												Edit Design
											</button>
											<button
												className={`neu prim ${editing ? " disabled" : ""}`}
												disabled={editing}
												onClick={() => {
													this.setState({
														formBuilderModalOpen: true,
														editingRegistrationForm: false,
													});
												}}
											>
												Edit Form
											</button>
										</div>
									</div>
									<div
										style={{
											maxWidth: "calc(100% - 15px)",
											minWidth: "calc(100% - 700px)",
											height: 'calc(100% - 45px)',
										}}
									>
										<div className="emailPreview eFrame pages flex-ac preview">
											{!pageBuilderModalOpen && (
												<PagePreviewFrame
													ENV={ENV}
													renderURL={`Organizations/${orgId}/accountPages/render/${bookingPage.page._id}/0`}
													preview={true}
													pageProperties={bookingPage.page}
													focusedComponent={""}
													simulatedUser={{}}
													simulatedSession={{ value: false }}
													renderOnly={true}
													editing={this.state.editCount}
												/>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}

				{selectedSection === "setup" && (
					<div className="sectionBody">

						<div className="blocksContainer">
							<div className="flex w-100 gap20">
								<div className="sectionBlock contained-h">

									<h2 className="blockHeader">Publishing Settings</h2>

									<div className="flex aic gap20">
										<InputField
											label="Booking Page Address"
											prefix={
												<span className="lightBody">
													{this.props.organization &&
														(this.props.organization.hostname
															? `https://${this.props.organization.hostname}/0/`
															: `https://${this.props.organization.identifier}.simple.events/0/`)}
												</span>
											}
											placeholder=""
											className="w-100"
											value={editedBookingPage.page.pathSlug}
											onChange={(e) => this.handlePageSlugChange(e)}
										/>
									</div>
									<ToggleField
										label="Any one with link can view the page and send booking requests"
										information="If not enabled, registrant must be an attendee of a connected event or a registered contact."
										checked={editedBookingPage.page.openToPublic}
										onChange={(e) =>
											this.setState({
												editedBookingPage: {
													...this.state.editedBookingPage,
													page: {
														...this.state.editedBookingPage.page,
														openToPublic: e.target.checked,
													},
												},
												editing: true,
											})
										}
										icons={false}
									/>

									<h2 className="blockHeader">Submission Confirmation Details</h2>
									<ToggleField
										label="Enable a review page before form submission"
										checked={
											editedBookingPage.form.enableSubmissionReview
										}
										icons={false}
										onChange={(e) => {
											this.setState({
												editedBookingPage: {
													...editedBookingPage,
													form: {
														...editedBookingPage.form,
														enableSubmissionReview: e.target.checked,
													},
												},
												editing: true,
											});
										}}
									/>

									{editedForm.enableSubmissionReview && (
										<div>
											<InputField
												label="Review Page Title"
												required={false}
												placeholder="Please Review"
												value={
													editedBookingPage.form.submissionReviewTitle
												}
												onChange={(e) => {
													this.setState({
														editedBookingPage: {
															...editedBookingPage,
															form: {
																...editedBookingPage.form,
																submissionReviewTitle: e.target.value,
															},
														},
														editing: true,
													});
												}}
											/>
											{/* Update */}
											<RichTextEditor
												id="Review_Page_Description"
												label="Review Page Description"
												placeholder="Look over your questions and go back if you need to edit."
												value={
													editedBookingPage.form.submissionReviewSubtitle
												}
												onChange={(e) => {
													this.setState({
														editedBookingPage: {
															...editedBookingPage,
															form: {
																...editedBookingPage.form,
																submissionReviewSubtitle: e,
															},
														},
														editing: true,
													});
												}}
												customFields={this.state.customFields}
											/>
										</div>
									)}

									<InputField
										label="Submission Success Title"
										required={false}
										placeholder="Form Submitted"
										value={editedBookingPage.form.successTitle}
										onChange={(e) => {
											this.setState({
												editedBookingPage: {
													...editedBookingPage,
													form: {
														...editedBookingPage.form,
														successTitle: e.target.value,
													}
												},
												editing: true,
											});
										}}
									/>
									<RichTextEditor
										id="Confirmation_Page_Description"
										label="Submission Success Description"
										placeholder="We have received your information"
										value={editedBookingPage.form.successSubtitle}
										onChange={(e) => {
											this.setState({
												editedBookingPage: {
													...editedBookingPage,
													form: {
														...editedBookingPage.form,
														successSubtitle: e,
													}
												},
												editing: true,
											});
										}}
									/>

									<ToggleField
										label="Enable Calendar Selection"
										information="Allow users to select a calendar rather than time slots"
										checked={editedBookingPage.enableCalendarSelection}
										onChange={(e) =>
											this.handleInputChange("enableCalendarSelection", e.target.checked)
										}
										icons={false}
									/>


								</div>
							</div>
						</div>
					</div>
				)}

				{selectedSection === "preview" && (
					<div className="sectionBody">
						<div className="blocksContainer">
							<div className="emailDesignPreview w-100 mt-10">
								<div
									style={{
										maxWidth: "calc(100% - 15px)",
										minWidth: "calc(100% - 700px)",
									}}
									className="cardDetailsContainerPages containerTitle mb-20"
								>
									<div className="w-100">
										<h2 className="blockHeader m-0">Booking Page Preview</h2>
										<p>Booking page as attendees will see</p>
									</div>
									<div className="flex aic gap10">
										<button
											className={`neu ${editing ? " disabled" : ""}`}
											disabled={editing}
											onClick={() => {
												this.setState({ pageBuilderModalOpen: true });
											}}
										>
											Edit Design
										</button>
										<button
											className={`neu prim ${editing ? " disabled" : ""}`}
											disabled={editing}
											onClick={() => {
												this.setState({
													formBuilderModalOpen: true,
													editingRegistrationForm: false,
												});
											}}
										>
											Edit Form
										</button>
									</div>
								</div>
								<div
									style={{
										maxWidth: "calc(100% - 15px)",
										minWidth: "calc(100% - 700px)",
										height: 'calc(100% - 45px)',
									}}
								>
									<div className="emailPreview eFrame pages flex-ac preview">
										{!pageBuilderModalOpen && (
											<PagePreviewFrame
												ENV={ENV}
												renderURL={`Organizations/${orgId}/accountPages/render/${bookingPage.page._id}/0`}
												preview={true}
												pageProperties={bookingPage.page}
												focusedComponent={""}
												simulatedUser={{}}
												simulatedSession={{ value: false }}
												renderOnly={true}
												editing={this.state.editCount}
											/>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				)}

				<SectionFooter
					updatedAt={bookingPage.updatedAt}
					saveEdit={this.saveBookingPage}
					cancelEdit={this.cancelEdit}
					editing={editing}
				/>

				{formBuilderModalOpen && (
					<FormBuilderModal
						isOpen={formBuilderModalOpen}
						editing={editing}
						toggle={this.toggleFormBuilderModal}
						form={form}
						editedForm={editedForm}
						fields={[]} // No custom fields for booking forms initially
						baseFieldList={bookingBaseFieldList}
						links={{ get: "" }}
						cancelEdit={() => {
							this.setState({
								editedForm: JSON.parse(JSON.stringify(this.state.form)),
								formBuilderModalOpen: false,
							});
						}}
						updateForm={this.updateForm}
						emails={[]} // No email integration at form level
						orgId={orgId}
						eventId={null} // Not event-based
						addNewField={() => { }} // Simplified for now
						saveForm={this.saveForm}
						customFields={[]}
						calendars={calendars}
						bookingPage={bookingPage}
					/>
				)}

				{pageBuilderModalOpen && (
					<PageEditor
						scope="account"
						isOpen={pageBuilderModalOpen}
						toggle={this.togglePageBuilderModal}
						lockForm={false}
						page={this.state.page}
						editedPage={editedPage}
						updatePage={this.updatePage}
						cancelEdit={() => {
							this.setState({
								editedPage: JSON.parse(JSON.stringify(this.state.page)),
								pageBuilderModalOpen: false,
							});
						}}
						bookingPage={bookingPage}
						pageId={editedPage._id}
						isAccountPage={true}
					/>
				)}
			</div>
		);
	}
}

export default withRouter(BookingFormBuilder);
